import React, { useState, useEffect, useRef, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  onAuthStateChanged,
  onFirebaseUserChanged,
  signIn,
  signOutUser,
  changeUserPassword,
  getCurrentUserToken,
  updateCurrentUserProfile,
  signInWithGoogle,
} from './firebaseAuth'

import Topbar from './components/Topbar'
import RegisterChoice from './pages/register/RegisterChoice'
import RegisterClient from './pages/register/RegisterClient'
import RegisterProvider from './pages/register/RegisterProvider'

import Login from './pages/login/Login'
import Home from './pages/home/Home'
import Marketplace from './pages/marketplace/Marketplace'
import RequestDetail from './pages/requests/RequestDetail'
import Profile from './pages/profile/Profile'
import UserPublicProfile from './pages/profile/UserPublicProfile'
import Dashboard from './pages/dashboard/Dashboard'
import AdminDashboard from './pages/admin/AdminDashboard'
import ClientWelcomePage from './pages/welcome/ClientWelcomePage'

import ListingDetail from './pages/listing/Listing'
import PublicationDetail from './pages/publications/PublicationDetail'
import ProviderLanding from './pages/provider/ProviderLanding'
import About from './pages/static/About'
import Privacy from './pages/static/Privacy'
import Terms from './pages/static/Terms'
import Cookies from './pages/static/Cookies'
import Advertising from './pages/static/Advertising'
import LoadingShowcase from './pages/static/LoadingShowcase'
import Tutorials from './pages/static/Tutorials'
import FaqPage from './pages/support/FaqPage'
import PlanCheckout from './pages/dashboard/subscriptions/PlanCheckout'
import CreditsCheckout from './pages/dashboard/subscriptions/CreditsCheckout'
import ReferralsPage from './pages/referrals/ReferralsPage'
import NewsPage from './pages/news/NewsPage'
import SettingsPage from './pages/settings/SettingsPage'
import { normalizeMojibake } from './utils/textNormalization'
import { usePageVisitTracker } from './hooks/usePageVisitTracker'

import './App.css'
import 'leaflet/dist/leaflet.css'
import {
  markNotificationsRead,
  markNotificationRead,
  deleteNotification,
  listCompanyNotifications,
} from './services/notificationsApi'
import { fetchCompanyContext } from './services/companyApi'
import { CheckCircle2, AlertCircle, Info, ArrowUpRight, X as CloseIcon } from 'lucide-react'
import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion'
import { ToastProvider, useToast } from './contexts/ToastContext'
import {
  resolveNotificationTarget,
  formatNotificationDate,
} from './utils/notificationHelpers'
import CookieConsent from './components/CookieConsent'
import AnalyticsManager from './components/AnalyticsManager'
import { syncCurrentUserSession, clearCurrentUserSession } from './services/userSessionsApi'

const countryCodes = [
  { code: '+52',  name: 'Mexico' },
  { code: '+1',   name: 'USA / Canada' },
  { code: '+44',  name: 'UK' },
  { code: '+91',  name: 'India' },
  { code: '+61',  name: 'Australia' },
  { code: '+49',  name: 'Germany' },
  { code: '+33',  name: 'France' },
  { code: '+81',  name: 'Japan' },
  { code: '+55',  name: 'Brazil' },
  { code: '+86',  name: 'China' },
  { code: '+20',  name: 'Egypt' },
  { code: '+972', name: 'Israel' },
  { code: '+34',  name: 'Spain' }
]

const LOGIN_REDIRECT_STORAGE_KEY = 'flotte:pendingProtectedRedirect'
const LOGIN_REDIRECT_TTL_MS = 5 * 60 * 1000
const NOTIFICATIONS_CENTER_PATH = '/dashboard?section=notificaciones'
const DEFAULT_TOAST_DURATION = 15000

function getPendingProtectedRedirect() {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY)
    if (!stored) return null
    const payload = JSON.parse(stored)
    if (!payload?.path) {
      window.sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
      return null
    }
    if (payload.timestamp && Date.now() - payload.timestamp > LOGIN_REDIRECT_TTL_MS) {
      window.sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
      return null
    }
    return payload
  } catch (error) {
    console.warn('No se pudo leer la redireccion pendiente de reserva.', error)
    window.sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
    return null
  }
}

function clearPendingProtectedRedirect() {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
}

function storePendingProtectedRedirect(path) {
  if (typeof window === 'undefined' || !path) return
  try {
    window.sessionStorage.setItem(
      LOGIN_REDIRECT_STORAGE_KEY,
      JSON.stringify({
        path,
        timestamp: Date.now(),
      })
    )
  } catch (error) {
    console.warn('No se pudo guardar la redireccion pendiente de reserva.', error)
  }
}

export default function App() {
  // Ã¢â‚¬â€ Estados de autenticaciÃƒÂ³n y formulario Ã¢â‚¬â€
  const [user, setUser]                         = useState(null)
  const [email, setEmail]                       = useState('')
  const [password, setPassword]                 = useState('')
  const [displayName, setDisplayName]           = useState('')
  const [selectedCountryCode, setSelectedCountryCode] = useState('+52')
  const [subscriberNumber, setSubscriberNumber] = useState('')
  const [authToken, setAuthToken]               = useState('')
  const [apiJwt, setApiJwt]                     = useState('')
  const [error, setError]                       = useState('')
  const [googleSignInPending, setGoogleSignInPending] = useState(false)
  const [authReady, setAuthReady]               = useState(false)
  const [theme, setTheme]                       = useState(() => {
    if (typeof window === 'undefined') return 'light'
    try {
      const storedTheme = window.localStorage.getItem('flotte-theme')
      if (storedTheme === 'light' || storedTheme === 'dark') {
        return storedTheme
      }
    } catch (storageError) {
      console.warn('No se pudo leer la preferencia de tema almacenada.', storageError)
    }
    return 'light'
  })
  const [supportAssistantContext, setSupportAssistantContext] = useState(null)
  const [companyNotifications, setCompanyNotifications]       = useState([])
  const [notificationsHydrated, setNotificationsHydrated]     = useState(false)
  const [globalToasts, setGlobalToasts]                       = useState([])
  const toastAudioContextRef = useRef(null)
  const toastSoundThrottledAtRef = useRef(0)
  const toastToneQueuedRef = useRef(false)
  const toastTimersRef = useRef(new Map())
  const lastTrackedSessionUserIdRef = useRef('')

  const resolveToastAudioContext = useCallback(() => {
    if (typeof window === 'undefined') return null
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) return null
    let audioContext = toastAudioContextRef.current
    if (!audioContext) {
      audioContext = new AudioCtx()
      toastAudioContextRef.current = audioContext
    }
    return audioContext
  }, [])

  const playToastTone = useCallback(() => {
    const audioContext = resolveToastAudioContext()
    if (!audioContext) return false
    const now = Date.now()
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {})
      return false
    }
    if (now - toastSoundThrottledAtRef.current < 150) return true
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {})
      return false
    }

    const baseTime = audioContext.currentTime + 0.02
    const scheduleTone = (frequency, offset = 0, detune = 0) => {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(frequency, baseTime + offset)
      if (detune) {
        oscillator.detune.setValueAtTime(detune, baseTime + offset)
      }
      gainNode.gain.setValueAtTime(0.0001, baseTime + offset)
      gainNode.gain.linearRampToValueAtTime(0.08, baseTime + offset + 0.02)
      gainNode.gain.exponentialRampToValueAtTime(0.0001, baseTime + offset + 0.28)
      oscillator.connect(gainNode).connect(audioContext.destination)
      oscillator.start(baseTime + offset)
      oscillator.stop(baseTime + offset + 0.32)
    }

    scheduleTone(640, 0, -5)
    scheduleTone(960, 0.12, 6)

    toastSoundThrottledAtRef.current = now
    toastToneQueuedRef.current = false
    return true
  }, [resolveToastAudioContext])

  const flushToastToneQueue = useCallback(() => {
    if (!toastToneQueuedRef.current) return
    const played = playToastTone()
    if (played) {
      toastToneQueuedRef.current = false
    }
  }, [playToastTone])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const events = ['pointerdown', 'keydown', 'touchstart']
    const listenerOptions = { passive: true }
    let unlocked = false
    const tryUnlock = () => {
      const ctx = resolveToastAudioContext()
      if (!ctx) return
      if (ctx.state === 'suspended') {
        ctx
          .resume()
          .then(() => handleContextUnlocked(ctx))
          .catch(() => {})
      } else {
        handleContextUnlocked(ctx)
      }
    }
    function handleContextUnlocked(ctx) {
      flushToastToneQueue()
      if (ctx?.state === 'running') {
        removeListeners()
      }
    }
    function removeListeners() {
      if (unlocked) return
      unlocked = true
      events.forEach(event => window.removeEventListener(event, tryUnlock, listenerOptions))
    }
    events.forEach(event => window.addEventListener(event, tryUnlock, listenerOptions))
    return () => {
      events.forEach(event => window.removeEventListener(event, tryUnlock, listenerOptions))
    }
  }, [resolveToastAudioContext, flushToastToneQueue])

  // Ã¢â‚¬â€ Escucha cambios de sesiÃƒÂ³n Ã¢â‚¬â€
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authRecord => {
      setUser(authRecord)
      setAuthToken('')
      setApiJwt(prev => (authRecord ? prev : ''))
      setError('')
      if (authRecord) {
        setDisplayName(authRecord.name || authRecord.username || authRecord.email || '')
      } else {
        setDisplayName('')
        setEmail('')
        setPassword('')
        setSelectedCountryCode('+52')
        setSubscriberNumber('')
      }
      setAuthReady(true)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = onFirebaseUserChanged(async firebaseUser => {
      if (!firebaseUser) {
        setApiJwt('')
        return
      }
      try {
        const token = await firebaseUser.getIdToken()
        setApiJwt(token)
      } catch (tokenError) {
        console.warn('No se pudo obtener el token de Firebase.', tokenError)
        setApiJwt('')
      }
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    let canceled = false

    if (!user?.id) {
      setCompanyNotifications([])
      setNotificationsHydrated(false)
      return () => {
        canceled = true
      }
    }

    setNotificationsHydrated(false)

    const loadInitialNotifications = async () => {
      try {
        const context = await fetchCompanyContext(user)
        if (canceled) return
        const companyId = context?.companyId || ''
        if (!companyId) {
          setCompanyNotifications([])
          setNotificationsHydrated(true)
          return
        }
        const items = await listCompanyNotifications({ companyId, limit: 50 })
        if (!canceled) {
          setCompanyNotifications(items)
          setNotificationsHydrated(true)
        }
      } catch (error) {
        console.warn('No se pudieron cargar las notificaciones iniciales.', error)
        if (!canceled) {
          setCompanyNotifications([])
          setNotificationsHydrated(true)
        }
      }
    }

    loadInitialNotifications()

    return () => {
      canceled = true
    }
  }, [user?.id, user])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (!user?.id) {
      const previousUserId = lastTrackedSessionUserIdRef.current
      if (previousUserId) {
        clearCurrentUserSession(previousUserId).catch(error => {
          console.warn('No se pudo limpiar la sesión local del usuario.', error)
        })
        lastTrackedSessionUserIdRef.current = ''
      }
      return undefined
    }

    lastTrackedSessionUserIdRef.current = user.id
    let canceled = false

    const syncSession = () => {
      if (canceled) return
      syncCurrentUserSession(user.id).catch(error => {
        console.warn('No se pudo sincronizar la sesión activa del usuario.', error)
      })
    }

    syncSession()
    const intervalId = window.setInterval(syncSession, 60 * 1000)

    return () => {
      canceled = true
      window.clearInterval(intervalId)
    }
  }, [user?.id])
  // Estado de tema claro/oscuro
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    root.setAttribute('data-theme', theme)
    if (theme === 'dark') {
      document.body.classList.add('theme-dark')
    } else {
      document.body.classList.remove('theme-dark')
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('flotte-theme', theme)
      } catch (storageError) {
        console.warn('No se pudo guardar la preferencia de tema.', storageError)
      }
    }
  }, [theme])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleStorage = event => {
      if (event.key !== 'flotte-theme') return
      if (event.newValue === 'light' || event.newValue === 'dark') {
        setTheme(event.newValue)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const handleToggleTheme = () => {
    setTheme(currentTheme => (currentTheme === 'dark' ? 'light' : 'dark'))
  }

  const handleCookieConsentUpdated = useCallback(preferences => {
    if (typeof window === 'undefined') return
    try {
      window.dispatchEvent(
        new CustomEvent('cookie-consent-updated', {
          detail: preferences,
        })
      )
    } catch (error) {
      console.warn('No se pudo propagar la actualización de consentimiento de cookies.', error)
    }
  }, [])

  // Funciones de signin, signout, etc.
  const handleSignIn = async () => {
    setError('')
    setApiJwt('')
    const normalizedEmail = (email || '').trim().toLowerCase()
    try {
      await signIn(normalizedEmail, password)
      setEmail('')
      setPassword('')
    } catch (err) {
      setError(`Sign In Failed: ${err.message}`)
    }
  }

  const handleGoogleSignIn = async (roleOrEvent = '') => {
    const role = typeof roleOrEvent === 'string' ? roleOrEvent : '';
    setError('')
    setGoogleSignInPending(true)
    try {
      await signInWithGoogle(role)
      setEmail('')
      setPassword('')
    } catch (err) {
      const detail = err?.message || 'No se pudo continuar con Google.'
      setError(detail)
    } finally {
      setGoogleSignInPending(false)
    }
  }

  const handleSignOut = async () => {
    setError('')
    const currentUserId = user?.id
    if (currentUserId) {
      try {
        await clearCurrentUserSession(currentUserId)
        lastTrackedSessionUserIdRef.current = ''
      } catch (sessionError) {
        console.warn('No se pudo limpiar la sesión activa antes de cerrar sesión.', sessionError)
      }
    }
    try {
      await signOutUser()
      setAuthToken('')
      setApiJwt('')
      setSupportAssistantContext(null)
      clearPendingProtectedRedirect()
    } catch (err) {
      setError(`Sign Out Failed: ${err.message}`)
    }
  }

  const handleShowAuthToken = async () => {
    setError('')
    try {
      const token = await getCurrentUserToken()
      setAuthToken(token || 'No se encontró un token vigente.')
    } catch (err) {
      setError(`Token Fetch Failed: ${err.message}`)
      setAuthToken('Error fetching token.')
    }
  }

  const handleUpdateProfile = async () => {
    setError('')
    try {
      await updateCurrentUserProfile(displayName)
      alert('Profile updated!')
    } catch (err) {
      setError(`Update Profile Failed: ${err.message}`)
    }
  }

  const [currentPwd, setCurrentPwd]               = useState('')
  const [newPwd, setNewPwd]                       = useState('')
  const [confirmPwd, setConfirmPwd]               = useState('')
  const [errorPassword, setErrorPassword]         = useState('')
  const [passwordSuccess, setPasswordSuccess]     = useState('')
  const [uploadingPassword, setUploadingPassword] = useState(false)

  const normalizedUserRole = String(user?.role || '').toLowerCase()
  const onboardingLandingPath =
    normalizedUserRole === 'client' || normalizedUserRole === 'provider'
      ? '/welcome'
      : '/dashboard'

  const clearPasswordFeedback = () => {
    setErrorPassword('')
    setPasswordSuccess('')
  }

  const handleChangePassword = async () => {
    clearPasswordFeedback()
    if (!currentPwd || !newPwd || !confirmPwd) {
      setErrorPassword('Completa todos los campos.')
      return
    }
    if (newPwd !== confirmPwd) {
      setErrorPassword('Las contraseÃ±as no coinciden.')
      return
    }
    if (newPwd.length < 8) {
      setErrorPassword('La nueva contraseÃ±a debe tener al menos 8 caracteres.')
      return
    }
    setUploadingPassword(true)
    try {
      await changeUserPassword(currentPwd, newPwd)
      setPasswordSuccess('Tu contraseÃ±a se actualizÃ³ correctamente.')
      setCurrentPwd('')
      setNewPwd('')
      setConfirmPwd('')
    } catch (err) {
      const message = err?.message || 'No se pudo actualizar la contraseÃ±a.'
      setErrorPassword(message)
    } finally {
      setUploadingPassword(false)
    }
  }

  const e164PhoneNumber = subscriberNumber
    ? `${selectedCountryCode}${subscriberNumber}`
    : ''

  const handleCompanyNotificationsOpen = () => {}

  const handleMarkNotificationRead = async id => {
    if (!id) return
    try {
      await markNotificationsRead([id])
    } catch (error) {
      console.warn('No se pudo marcar la notificacion como leida.', error)
    }
    setCompanyNotifications(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev
      let hasChanges = false
      const next = prev.map(item => {
        if (item?.id === id && !item.read) {
          hasChanges = true
          return { ...item, read: true }
        }
        return item
      })
      return hasChanges ? next : prev
    })
  }

  const handleMarkNotificationUnread = async id => {
    if (!id) return
    try {
      await markNotificationRead(id, false)
    } catch (error) {
      console.warn('No se pudo marcar la notificacion como no leida.', error)
    }
    setCompanyNotifications(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev
      let hasChanges = false
      const next = prev.map(item => {
        if (item?.id === id && item.read) {
          hasChanges = true
          return { ...item, read: false }
        }
        return item
      })
      return hasChanges ? next : prev
    })
  }

  const handleRemoveNotification = async id => {
    if (!id) return
    try {
      await deleteNotification(id)
    } catch (error) {
      console.warn('No se pudo eliminar la notificacion.', error)
    }
    setCompanyNotifications(prev =>
      Array.isArray(prev) ? prev.filter(item => item?.id !== id) : prev
    )
  }

  const handleMarkAllNotificationsRead = async () => {
    const source = Array.isArray(companyNotifications) ? companyNotifications : []
    const unreadIds = source
      .filter(item => item && !item.read)
      .map(item => item.id)
      .filter(Boolean)

    if (!unreadIds.length) return

    try {
      await markNotificationsRead(unreadIds)
    } catch (error) {
      console.warn('No se pudo marcar todas las notificaciones como leidas.', error)
    }

    setCompanyNotifications(prev => {
      if (!Array.isArray(prev) || !prev.length) return prev
      let hasChanges = false
      const next = prev.map(item => {
        if (item && !item.read) {
          hasChanges = true
          return { ...item, read: true }
        }
        return item
      })
      return hasChanges ? next : prev
    })
  }
  const releaseToastTimer = id => {
    if (!id) return
    const entry = toastTimersRef.current.get(id)
    if (entry?.timeoutId) {
      clearTimeout(entry.timeoutId)
    }
    toastTimersRef.current.delete(id)
  }

  const handleDismissToast = id => {
    if (!id) return
    releaseToastTimer(id)
    setGlobalToasts(prev => (Array.isArray(prev) ? prev.filter(item => item.id !== id) : prev))
  }

  const scheduleToastAutoClose = (id, duration) => {
    if (typeof window === 'undefined' || !id) return
    const normalizedDuration = typeof duration === 'number' ? duration : DEFAULT_TOAST_DURATION
    if (normalizedDuration <= 0) {
      handleDismissToast(id)
      return
    }
    releaseToastTimer(id)
    const timeoutId = window.setTimeout(() => {
      handleDismissToast(id)
    }, normalizedDuration)
    toastTimersRef.current.set(id, {
      timeoutId,
      duration: normalizedDuration,
      remaining: normalizedDuration,
      startedAt: Date.now(),
      paused: false,
    })
  }

  const pauseToastAutoClose = id => {
    if (typeof window === 'undefined' || !id) return
    const entry = toastTimersRef.current.get(id)
    if (!entry || entry.paused) return
    if (entry.timeoutId) {
      clearTimeout(entry.timeoutId)
    }
    const elapsed = Date.now() - (entry.startedAt || Date.now())
    const remainingBase =
      typeof entry.remaining === 'number' ? entry.remaining : entry.duration
    const remaining = Math.max(remainingBase - elapsed, 0)
    toastTimersRef.current.set(id, {
      ...entry,
      timeoutId: null,
      remaining,
      paused: true,
    })
  }

  const resumeToastAutoClose = id => {
    if (typeof window === 'undefined' || !id) return
    const entry = toastTimersRef.current.get(id)
    if (!entry) return
    const remaining = typeof entry.remaining === 'number' ? entry.remaining : entry.duration
    if (remaining <= 0) {
      handleDismissToast(id)
      return
    }
    if (entry.timeoutId && !entry.paused) return
    const timeoutId = window.setTimeout(() => {
      handleDismissToast(id)
    }, remaining)
    toastTimersRef.current.set(id, {
      ...entry,
      timeoutId,
      startedAt: Date.now(),
      paused: false,
    })
  }

  const handlePushToast = toast => {
    if (!toast) return
    const id = toast.id || `toast-${Date.now()}`
    const duration = typeof toast.duration === 'number' ? toast.duration : DEFAULT_TOAST_DURATION
    setGlobalToasts(prev => {
      const base = Array.isArray(prev) ? [...prev] : []
      return [...base, { ...toast, id, duration }]
    })
    const playedTone = playToastTone()
    if (!playedTone) {
      toastToneQueuedRef.current = true
    }
    if (toast.autoClose !== false) {
      scheduleToastAutoClose(id, duration)
    }
  }

  useEffect(() => {
    const timersOnMount = toastTimersRef.current
    return () => {
      timersOnMount.forEach(entry => {
        if (entry?.timeoutId) {
          clearTimeout(entry.timeoutId)
        }
      })
      timersOnMount.clear()
    }
  }, [])

  return (
    <ToastProvider pushToast={handlePushToast}>
      <div className="App">
        <Router>
          <AnalyticsManager />
          <UnreadNotificationToasts
            userId={user?.id}
            notifications={companyNotifications}
            hydrated={notificationsHydrated}
          />
          <ScrollToTopOnRouteChange />
          <PostLoginRedirect user={user} />
          <RouteVisitTracker user={user} enabled={authReady} />
          <Topbar
            user={user}
            onSignOut={handleSignOut}
            theme={theme}
            onToggleTheme={handleToggleTheme}
            supportAssistantContext={supportAssistantContext}
            companyNotifications={companyNotifications}
            notificationsHydrated={notificationsHydrated}
            onCompanyNotificationsOpen={handleCompanyNotificationsOpen}
            onNotificationRead={handleMarkNotificationRead}
            onNotificationUnread={handleMarkNotificationUnread}
            onNotificationDelete={handleRemoveNotification}
            onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
          />

          <AppShell>
            <Routes>
          {/* 1. Home siempre en "/" */}
          <Route path="/" element={<Home user={user} />} />

          {/* 2. Marketplace en "/marketplace" (publico) */}
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/solicitudes" element={<Navigate to="/marketplace?segment=requests" replace />} />
          <Route path="/solicitudes/:id" element={<RequestDetail />} />

          {/* 2b. Detalle de publicaciones creadas desde el panel */}
          <Route
            path="/publicaciones/:id/reservar"
            element={
              <ProtectedRoute user={user} authReady={authReady}>
                <PublicationDetail />
              </ProtectedRoute>
            }
          />
          <Route path="/publicaciones/:id" element={<PublicationDetail />} />

          {/* 3. Detalle de publicaciÃƒÂ³n */}
          {/* PÃƒÂºblica/SEO por slug */}
          <Route path="/listing/:slug" element={<ListingDetail />} />
          {/* TÃƒÂ©cnica por id */}
          <Route path="/listing/id/:id" element={<ListingDetail />} />

          {/* 4. Login en "/login" si NO hay usuario */}
          <Route
            path="/login"
            element={
              !user ? (
                <Login
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  displayName={displayName}
                  setDisplayName={setDisplayName}
                  selectedCountryCode={selectedCountryCode}
                  setSelectedCountryCode={setSelectedCountryCode}
                  subscriberNumber={subscriberNumber}
                  setSubscriberNumber={setSubscriberNumber}
                  handleSignIn={handleSignIn}
                  e164PhoneNumber={e164PhoneNumber}
                  error={error}
                  countryCodes={countryCodes}
                  handleGoogleSignIn={handleGoogleSignIn}
                  googleSignInPending={googleSignInPending}
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          {/* 4b. Registro */}
          <Route
            path="/register"
            element={!user ? <RegisterChoice /> : <Navigate to={onboardingLandingPath} replace />}
          />
          <Route
            path="/register/client"
            element={
              !user ? (
                <RegisterClient
                  onGoogleSignIn={() => handleGoogleSignIn('customer')}
                  googleSignInPending={googleSignInPending}
                />
              ) : (
                <Navigate to={onboardingLandingPath} replace />
              )
            }
          />
          <Route
            path="/register/provider"
            element={
              !user ? (
                <RegisterProvider
                  onGoogleSignIn={() => handleGoogleSignIn('provider')}
                  googleSignInPending={googleSignInPending}
                />
              ) : (
                <Navigate to={onboardingLandingPath} replace />
              )
            }
          />
          <Route path="/register-choice" element={<Navigate to="/register" replace />} />

          <Route
            path="/welcome"
            element={
              <ProtectedRoute
                user={user}
                authReady={authReady}
                requiredRoles={['client', 'provider']}
                rememberPath={false}
              >
                <ClientWelcomePage user={user} />
              </ProtectedRoute>
            }
          />
          <Route path="/welcome/cliente" element={<Navigate to="/welcome" replace />} />
          <Route path="/welcome/proveedor" element={<Navigate to="/welcome" replace />} />

          <Route
            path="/referrals"
            element={
              <ProtectedRoute user={user} authReady={authReady}>
                <ReferralsPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute user={user} authReady={authReady}>
                <SettingsPage user={user} />
              </ProtectedRoute>
            }
          />

          {/* 5. Dashboard en "/dashboard" si HAY usuario */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute user={user} authReady={authReady}>
                <Dashboard
                  user={user}
                  displayName={displayName}
                  setDisplayName={setDisplayName}
                  handleUpdateProfile={handleUpdateProfile}
                  handleSignOut={handleSignOut}
                  handleShowAuthToken={handleShowAuthToken}
                  authToken={authToken}
                  apiJwt={apiJwt}
                  e164PhoneNumber={e164PhoneNumber}
                  error={error}
                  currentPwd={currentPwd}
                  setCurrentPwd={setCurrentPwd}
                  newPwd={newPwd}
                  setNewPwd={setNewPwd}
                  confirmPwd={confirmPwd}
                  setConfirmPwd={setConfirmPwd}
                  handleChangePassword={handleChangePassword}
                  uploadingPassword={uploadingPassword}
                  errorPassword={errorPassword}
                  passwordSuccess={passwordSuccess}
                  clearPasswordFeedback={clearPasswordFeedback}
                  onSupportAssistantContextChange={setSupportAssistantContext}
                  companyNotifications={companyNotifications}
                  onCompanyNotificationsChange={setCompanyNotifications}
                />
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/plan/:planSlug"
            element={
              <ProtectedRoute user={user} authReady={authReady}>
                <PlanCheckout />
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/credits"
            element={
              <ProtectedRoute user={user} authReady={authReady}>
                <CreditsCheckout />
              </ProtectedRoute>
            }
          />

          {/* 5b. Admin Dashboard solo para administradores */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute
                user={user}
                authReady={authReady}
                requiredRoles={['admin']}
              >
                <AdminDashboard user={user} />
              </ProtectedRoute>
            }
          />

          {/* 6. Perfil pÃƒÂºblico por slug */}
          <Route path="/empresa/:slug" element={<Profile />} />
          <Route path="/usuario/:username" element={<UserPublicProfile />} />

          {/* 6b. Aterrizaje para nuevos proveedores */}
          <Route path="/proveedor/unete" element={<ProviderLanding />} />

          {/* 6c. Paginas informativas */}
          <Route path="/sobre-nosotros" element={<About />} />
          <Route path="/privacidad" element={<Privacy />} />
          <Route path="/terminos" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/publicidad" element={<Advertising />} />
          <Route path="/tutoriales" element={<Tutorials />} />
          <Route path="/ayuda/preguntas-frecuentes" element={<FaqPage />} />
          <Route path="/noticias" element={<NewsPage />} />
          <Route path="/loading-preview" element={<LoadingShowcase />} />

          {/* 7. Cualquier otra ruta redirige a Home */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppShell>
        <CookieConsent onConsentUpdated={handleCookieConsentUpdated} />
        {globalToasts.length ? (
          <div className="global-toast-stack">
            <AnimatePresence initial={false}>
              {globalToasts.map(toast => {
                const toastTitle =
                  normalizeMojibake(toast.title) || 'Nueva notificación'
                const toastSubtitle = normalizeMojibake(toast.subtitle)
                const toastMessage =
                  normalizeMojibake(toast.message) ||
                  'Tienes una nueva actividad en Flotte.'
                const toastCtaLabel = toast.ctaLabel
                  ? normalizeMojibake(toast.ctaLabel)
                  : ''

                const rawVariant = (toast.variant || toast.status || toast.type || 'info')?.toString().toLowerCase()
                const supportedVariants = ['success', 'error', 'warning', 'info']
                const toastVariant = supportedVariants.includes(rawVariant) ? rawVariant : 'info'
                const variantIcons = {
                  success: CheckCircle2,
                  error: AlertCircle,
                  warning: AlertCircle,
                  info: Info,
                }
                const VariantIcon = variantIcons[toastVariant] || Info

                const isActionable = typeof toast.onCta === 'function'
                const autoCloseEnabled = toast.autoClose !== false
                const toastDurationMs =
                  typeof toast.duration === 'number' ? toast.duration : DEFAULT_TOAST_DURATION

                return (
                  <GlobalToastCard
                    key={toast.id}
                    toastId={toast.id}
                    toastVariant={toastVariant}
                    toastTitle={toastTitle}
                    toastSubtitle={toastSubtitle}
                    toastMessage={toastMessage}
                    toastCtaLabel={toastCtaLabel}
                    VariantIcon={VariantIcon}
                    isActionable={isActionable}
                    autoCloseEnabled={autoCloseEnabled}
                    toastDurationMs={toastDurationMs}
                    onAction={
                      isActionable
                        ? () => {
                            toast.onCta()
                            handleDismissToast(toast.id)
                          }
                        : null
                    }
                    onDismiss={() => handleDismissToast(toast.id)}
                    pauseAutoClose={() => pauseToastAutoClose(toast.id)}
                    resumeAutoClose={() => resumeToastAutoClose(toast.id)}
                  />
                )
              })}
            </AnimatePresence>
          </div>
        ) : null}
        </Router>
      </div>
    </ToastProvider>
  )
}

function GlobalToastCard({
  toastId,
  toastVariant,
  toastTitle,
  toastSubtitle,
  toastMessage,
  toastCtaLabel,
  VariantIcon,
  isActionable,
  autoCloseEnabled,
  toastDurationMs,
  onAction,
  onDismiss,
  pauseAutoClose,
  resumeAutoClose,
}) {
  const x = useMotionValue(0)
  const opacity = useTransform(x, [0, 200], [1, 0], { clamp: true })
  const [isDragging, setIsDragging] = useState(false)
  const dragSuppressedClickRef = useRef(false)

  const role = isActionable ? 'button' : 'status'
  const tabIndex = isActionable ? 0 : -1
  const ariaLabel = isActionable ? toastCtaLabel || `Abrir ${toastTitle}` : undefined

  const handlePointerDown = () => {
    dragSuppressedClickRef.current = false
  }

  const handleDragStart = () => {
    setIsDragging(true)
    dragSuppressedClickRef.current = false
    pauseAutoClose?.()
  }

  const handleDrag = (_event, info) => {
    if (dragSuppressedClickRef.current) return
    if (Math.abs(info.offset.x) > 5 || Math.abs(info.offset.y) > 5) {
      dragSuppressedClickRef.current = true
    }
  }

  const handleDragEnd = (_event, info) => {
    setIsDragging(false)
    const shouldDismiss = info.offset.x > 100 || info.velocity.x > 800
    if (shouldDismiss) {
      onDismiss?.()
      return
    }
    resumeAutoClose?.()
  }

  const handleCardClick = () => {
    if (!isActionable || typeof onAction !== 'function') return
    if (dragSuppressedClickRef.current) {
      dragSuppressedClickRef.current = false
      return
    }
    onAction()
  }

  const handleKeyDown = event => {
    if (!isActionable || typeof onAction !== 'function') return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onAction()
    }
  }

  const handleFocus = () => {
    if (!autoCloseEnabled) return
    pauseAutoClose?.()
  }

  const handleBlur = event => {
    if (!autoCloseEnabled) return
    if (event.currentTarget.contains(event.relatedTarget)) {
      return
    }
    resumeAutoClose?.()
  }

  const handleMouseEnter = () => {
    if (!autoCloseEnabled) return
    pauseAutoClose?.()
  }

  const handleMouseLeave = () => {
    if (!autoCloseEnabled) return
    resumeAutoClose?.()
  }

  return (
    <motion.div
      layout
      drag="x"
      dragConstraints={{ left: 0, right: 320 }}
      dragElastic={0.2}
      dragMomentum
      dragSnapToOrigin
      data-toast-id={toastId}
      className={`global-toast-card global-toast-card--${toastVariant}${
        isActionable ? ' global-toast-card--actionable' : ''
      }${isDragging ? ' global-toast-card--dragging' : ''}`}
      aria-live="assertive"
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onKeyDown={isActionable ? handleKeyDown : undefined}
      onClick={handleCardClick}
      onPointerDown={handlePointerDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      style={{ x, opacity }}
      initial={{ opacity: 0, x: 24, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{
        opacity: 0,
        x: 260,
        height: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
        borderWidth: 0,
      }}
      transition={{ type: 'spring', stiffness: 400, damping: 40, mass: 1 }}
    >
      <div className="global-toast-icon" aria-hidden="true">
        <VariantIcon size={20} strokeWidth={2.2} />
      </div>
      <div className="global-toast-content">
        <div className="global-toast-title-row">
          <strong className="global-toast-title">
            {toastTitle}
            {isActionable ? (
              <span className="global-toast-arrow" aria-hidden="true">
                <ArrowUpRight size={14} />
              </span>
            ) : null}
          </strong>
          {toastSubtitle ? (
            <span className="global-toast-time">{toastSubtitle}</span>
          ) : null}
        </div>
        <p className="global-toast-message">{toastMessage}</p>
      </div>
      <button
        type="button"
        className="global-toast-close"
        aria-label="Cerrar notificación"
        onClick={event => {
          event.stopPropagation()
          onDismiss?.()
        }}
      >
        <CloseIcon size={16} />
      </button>
      {autoCloseEnabled ? (
        <span
          className="global-toast-progress"
          aria-hidden="true"
          style={{ animationDuration: `${toastDurationMs}ms` }}
        />
      ) : null}
    </motion.div>
  )
}

function UnreadNotificationToasts({ userId, notifications, hydrated }) {
  const navigate = useNavigate()
  const pushToast = useToast()
  const bootstrappedUserRef = useRef(null)
  const announcedIdsRef = useRef(new Set())
  const summaryAnnouncedUserRef = useRef(null)

  useEffect(() => {
    if (!userId) {
      bootstrappedUserRef.current = null
      announcedIdsRef.current = new Set()
      summaryAnnouncedUserRef.current = null
      return
    }
    if (!hydrated) return
    if (typeof pushToast !== 'function') return

    if (bootstrappedUserRef.current !== userId) {
      announcedIdsRef.current = new Set()
      summaryAnnouncedUserRef.current = null
    }

    const source = Array.isArray(notifications) ? notifications : []
    if (!source.length) {
      return
    }

    const unread = source.filter(item => item && !item.read)
    if (!unread.length) {
      return
    }

    const shouldShowSummary = summaryAnnouncedUserRef.current !== userId
    if (shouldShowSummary) {
      const unreadCount = unread.length
      const label = unreadCount === 1 ? 'notificación' : 'notificaciones'
      pushToast({
        id: `notification-summary-${userId}`,
        title: 'Tienes notificaciones sin leer',
        subtitle: 'Centro de notificaciones',
        message: `Tienes ${unreadCount} ${label} sin leer.`,
        variant: 'info',
        ctaLabel: 'Ver notificaciones',
        onCta: () => navigate(NOTIFICATIONS_CENTER_PATH),
      })
      summaryAnnouncedUserRef.current = userId
      unread.forEach(notification => {
        const notificationId = notification?.id
        if (notificationId) {
          announcedIdsRef.current.add(notificationId)
        }
      })
      bootstrappedUserRef.current = userId
      return
    }

    unread.forEach(notification => {
      const notificationId = notification?.id
      if (!notificationId || announcedIdsRef.current.has(notificationId)) {
        return
      }
      const payload = buildNotificationToastPayload(notification, navigate)
      if (!payload) return
      announcedIdsRef.current.add(notificationId)
      pushToast(payload)
    })

    bootstrappedUserRef.current = userId
  }, [userId, notifications, hydrated, navigate, pushToast])

  return null
}

function buildNotificationToastPayload(notification, navigate) {
  if (!notification) return null
  const actor =
    normalizeMojibake(
      notification.expand?.createdBy?.name ||
        notification.expand?.createdBy?.username ||
        notification.expand?.createdBy?.email
    ) ||
    normalizeMojibake(notification.title) ||
    'Nueva notificación'
  const message =
    normalizeMojibake(notification.message) ||
    normalizeMojibake(notification.title) ||
    'Tienes una nueva actividad en Flotte.'
  const subtitle =
    formatNotificationDate(notification.createdAt) || 'Hace un momento'
  const target = resolveNotificationTarget(notification)
  const variant = resolveNotificationToastVariant(notification?.type || notification?.status)

  return {
    id: notification.id ? `notification-${notification.id}` : undefined,
    title: actor,
    subtitle,
    message,
    variant,
    onCta: target
      ? () => {
          navigate(target)
        }
      : undefined,
  }
}

function resolveNotificationToastVariant(typeValue) {
  const normalized = String(typeValue || '').toLowerCase()
  if (['success', 'completed', 'done', 'approved', 'ok'].includes(normalized)) {
    return 'success'
  }
  if (['error', 'failed', 'denied', 'rejected', 'danger'].includes(normalized)) {
    return 'error'
  }
  if (['warning', 'pending', 'review', 'alert'].includes(normalized)) {
    return 'warning'
  }
  return 'info'
}

function AppShell({ children }) {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const flushRoutes = ['/', '/login']
  const shouldFlush = flushRoutes.includes(pathname) || pathname.startsWith('/register')
  const className = shouldFlush ? 'app-shell app-shell--flush' : 'app-shell'

  return (
    <main className={className}>
      {children}
    </main>
  )
}

function ProtectedRoute({
  user,
  authReady,
  children,
  rememberPath = true,
  requiredRoles = null,
  disallowedRoles = null,
  fallbackPath = '/dashboard',
  redirectPath = '/login',
}) {
  const location = useLocation()
  const targetPath = `${location.pathname}${location.search || ''}${location.hash || ''}`

  useEffect(() => {
    if (!rememberPath || !authReady || user) return
    storePendingProtectedRedirect(targetPath)
  }, [rememberPath, authReady, user, targetPath])

  if (!authReady) {
    return null
  }

  if (!user) {
    return (
      <Navigate
        to={redirectPath}
        replace
        state={{ fromProtectedRoute: true, target: targetPath }}
      />
    )
  }

  const normalizedRole = String(user.role || '').toLowerCase()
  if (Array.isArray(requiredRoles) && requiredRoles.length) {
    const allowed = requiredRoles.map(role => String(role || '').toLowerCase())
    if (!allowed.includes(normalizedRole)) {
      return fallbackPath ? <Navigate to={fallbackPath} replace /> : null
    }
  }

  if (Array.isArray(disallowedRoles) && disallowedRoles.length) {
    const blocked = disallowedRoles.map(role => String(role || '').toLowerCase())
    if (blocked.includes(normalizedRole)) {
      return fallbackPath ? <Navigate to={fallbackPath} replace /> : null
    }
  }

  return children
}

function PostLoginRedirect({ user }) {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!user) return
    const pending = getPendingProtectedRedirect()
    if (!pending?.path) return

    if (pending.path === location.pathname) {
      clearPendingProtectedRedirect()
      return
    }

    clearPendingProtectedRedirect()
    navigate(pending.path, { replace: true })
  }, [user, navigate, location.pathname])

  return null
}

function ScrollToTopOnRouteChange() {
  const location = useLocation()

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname, location.search])

  return null
}

function RouteVisitTracker({ user, enabled }) {
  usePageVisitTracker({ user, enabled })
  return null
}
